/**
 * This script is used ONLY for Gainsight.
 * Its purpose is to resize grids in tour preview mode since JS code is responsible for grid resizing and rrweb doesn't capture JS code.
 * It takes initial grid item positions, calculates offsetWidth of a grid, and shrinks grid items to fit the screen
 */

import { debounce } from 'lodash-es';

const GAINSIGHT_CLASS_NAME = 'gainsight-app';
const GRID_ITEM_SPACE = 15;

const resizeSVGsInElement = (element, ratio) => {
  const shadowHosts = element.querySelectorAll('*');
  shadowHosts.forEach((shadowHost) => {
    if (shadowHost.shadowRoot) {
      resizeSVGsInShadowDom(shadowHost, ratio);
    }
  });
};
const getTranslatedEndPosition = (element) => {
  const style = window.getComputedStyle(element);
  const transform = style.transform;

  // The 4th and 5th values in matrix correspond to the translateX and translateY values
  const match = /^matrix\((.+)\)$/.exec(transform);
  if (match) {
    const matrixValues = match[1].split(', ').map(Number.parseFloat);
    const translateX = matrixValues[4];
    const translateY = matrixValues[5];
    const endLeft = translateX;
    const endTop = translateY;

    return { endLeft: endLeft, endTop: endTop };
  }
  return null;
};
const fitGridElementsToScreen = (
  gridElements,
  viewportWidth,
  prevGridWidth
) => {
  const resizeRatio = viewportWidth / prevGridWidth;
  gridElements.forEach((element) => {
    const newWidth = viewportWidth * resizeRatio;
    if (!element.offsetLeft && !element.offsetTop) {
      const endPosition = getTranslatedEndPosition(element);
      if (endPosition) {
        element.style.left = `${endPosition.endLeft}px`;
        element.style.top = `${endPosition.endTop}px`;
        element.style.transform = '';
      }
    }
    // Don't change this. It seems like it's sufficient with the if clause above
    element.style.left = `${element.offsetLeft * resizeRatio}px`;
    element.style.top = `${element.offsetTop * resizeRatio}px`;

    resizeSVGsInElement(element, newWidth / element.offsetWidth);
    element.style.width = `${element.offsetWidth * resizeRatio}px`;
    element.style.height = `${element.offsetHeight * resizeRatio}px`;
  });
};

const resizeSVGsInShadowDom = (hostElement, ratio) => {
  const ignoredSVGDataIcons = [
    'images:trueNPS',
    'chevron-down',
    'images:csat',
    'images:ai',
  ];
  const svgElements = hostElement.shadowRoot?.querySelectorAll('svg') || [];

  svgElements.forEach((svg) => {
    if (ignoredSVGDataIcons.includes(svg.getAttribute('data-icon'))) {
      return;
    }
    svg.style.width = '100%';
    svg.style.height = 'auto';
    svg.parentNode.style.width = '100%';
    svg.parentNode.style.height = 'auto';
  });

  const shadowHosts = hostElement?.shadowRoot?.querySelectorAll('*') || [];
  shadowHosts.forEach((shadowHost) => resizeSVGsInShadowDom(shadowHost, ratio));
};

const getSumOfWidthsInFirstRow = (gridItems) => {
  if (gridItems.length === 0) {
    return 0;
  }

  const firstItemTop = gridItems[0].getBoundingClientRect().top;
  let sumOfWidths = 0;
  for (let i = 0; i < gridItems.length; i++) {
    const currentItemRect = gridItems[i].getBoundingClientRect();
    const currentItemTop = currentItemRect.top;
    const currentItemWidth = currentItemRect.width;
    if (Math.abs(currentItemTop - firstItemTop) < 1) {
      sumOfWidths += currentItemWidth + GRID_ITEM_SPACE;
    } else {
      break;
    }
  }

  return sumOfWidths;
};

const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const resize = (doc) => {
  window.requestAnimationFrame(() => {
    const grids = doc?.getElementsByTagName('gridster');
    if (!grids.length) {
      return;
    }
    const gridsArr = [].slice.call(grids);
    gridsArr.forEach((grid) => {
      if (isInViewport(grid)) {
        // Only deal with grids that are in viewport
        grid.style.width = '100%';
        const items = grid?.getElementsByTagName('gridster-item') || [];
        const arrItems = [].slice.call(items);
        const gridWidth = getSumOfWidthsInFirstRow(items);
        fitGridElementsToScreen(arrItems, grid?.offsetWidth || 0, gridWidth);
      }
    });
  });
};

export const initGridResizer = (doc) => {
  // Apply client specific fix only for Gainsight client
  if (!doc?.body?.classList?.contains(GAINSIGHT_CLASS_NAME)) {
    return;
  }

  resize(doc);
  window.onresize = debounce(() => {
    resize(doc);
  }, 500);
};
